/**
 * Path → page-name mapping for the Recently Visited widget.
 *
 * A module of its own, with ZERO imports, for the reason `subscriptionLifecycle.ts`
 * is: pure data does not belong inside a hook, and a test for it should not have to
 * load one.
 *
 * It was originally extracted for a second reason that no longer holds: importing
 * `useRecentlyVisited.ts` pulled in `react-router-dom`, which Jest 27 could not
 * resolve, and the repo's other answer to that — `jest.mock(…, { virtual: true })`
 * — leaks ACROSS test files under `--runInBand` (svdy-ui §12.11). That resolution
 * gap was fixed at the root on 2026-08-06 (two `moduleNameMapper` entries plus a
 * TextEncoder polyfill; the package's `main` pointed at a file absent from the
 * tarball). The extraction stands on the first reason alone.
 *
 * `src/i18n/recentlyVisitedTitles.test.ts` asserts every route under `MainLayout`
 * resolves here to a key present in all nine locales.
 */

// ---------------------------------------------------------------------------
// Path → title mapping. Add a new entry when a new top-level route lands.
// Falls back to a humanized version of the last path segment.
//
// 2026-05-16 audit: previous map had 8 stale routes (e.g. /organization/branches
// when the actual route is /organization/branches-locations) plus
// fall-throughs that surfaced misleading titles for sub-pages
// (/attendance/leave matched as 'Public Holidays' for every leave
// sub-route). Rewrote against the App.tsx route table to land each
// real path on the right human-readable label, ordering specific
// before generic so e.g. /attendance/leave/policies wins over the
// /attendance/leave fallback.
// ---------------------------------------------------------------------------

export const TITLE_MAP: Array<{ test: RegExp; i18nKey: string }> = [
  { test: /^\/dashboard/, i18nKey: 'dashboard' },

  // Account — the only top-level route under MainLayout with no side menu of its
  // own, which is also why it was the one route missing from this list until
  // 2026-08-01: it is not reachable from any menu, so nothing prompted an entry.
  // Named to stay distinct from `securitySettings`, the ORG-level page.
  { test: /^\/account\/security/, i18nKey: 'accountSecurity' },

  // Organization
  { test: /^\/organization\/profile/, i18nKey: 'orgProfile' },
  { test: /^\/organization\/branches-locations/, i18nKey: 'branchesLocations' },
  { test: /^\/organization\/departments/, i18nKey: 'departments' },
  { test: /^\/organization\/positions/, i18nKey: 'positions' },
  { test: /^\/organization\/employment-types/, i18nKey: 'employmentTypes' },
  { test: /^\/organization\/workers\/documents/, i18nKey: 'workerDocuments' },
  { test: /^\/organization\/workers\/face-templates/, i18nKey: 'faceTemplates' },
  { test: /^\/organization\/workers\/active/, i18nKey: 'activeWorkers' },
  { test: /^\/organization\/workers\/former/, i18nKey: 'formerWorkers' },
  { test: /^\/organization\/workers/, i18nKey: 'workers' },
  { test: /^\/organization\/worker-groups/, i18nKey: 'workerGroups' },
  { test: /^\/organization\/chart/, i18nKey: 'orgChart' },
  { test: /^\/organization\/events/, i18nKey: 'events' },
  { test: /^\/organization\/announcements/, i18nKey: 'announcements' },
  { test: /^\/organization/, i18nKey: 'organization' },

  // Attendance
  { test: /^\/attendance\/overview/, i18nKey: 'attendanceOverview' },
  { test: /^\/attendance\/rules\/global/, i18nKey: 'globalRules' },
  { test: /^\/attendance\/rules\/department/, i18nKey: 'departmentRules' },
  { test: /^\/attendance\/rules\/group/, i18nKey: 'groupRules' },
  { test: /^\/attendance\/shifts\/breaks/, i18nKey: 'breaks' },
  { test: /^\/attendance\/shifts\/timetables/, i18nKey: 'timetables' },
  { test: /^\/attendance\/shifts\/cycles/, i18nKey: 'shiftCycles' },
  { test: /^\/attendance\/schedules\/department/, i18nKey: 'departmentSchedules' },
  { test: /^\/attendance\/schedules\/worker/, i18nKey: 'workerSchedules' },
  { test: /^\/attendance\/schedules\/temporary/, i18nKey: 'temporarySchedules' },
  { test: /^\/attendance\/schedules/, i18nKey: 'scheduleView' },
  { test: /^\/attendance\/leave\/types/, i18nKey: 'leaveTypes' },
  { test: /^\/attendance\/leave\/policies/, i18nKey: 'leavePolicies' },
  { test: /^\/attendance\/leave\/public-holidays/, i18nKey: 'publicHolidays' },
  { test: /^\/attendance\/leave\/balances/, i18nKey: 'leaveBalances' },
  { test: /^\/attendance\/leave/, i18nKey: 'leaveManagement' },
  { test: /^\/attendance\/geofences\/department/, i18nKey: 'departmentGeofences' },
  { test: /^\/attendance\/geofences\/group/, i18nKey: 'groupGeofences' },
  { test: /^\/attendance\/geofences\/worker/, i18nKey: 'workerGeofences' },
  { test: /^\/attendance\/time-entries/, i18nKey: 'timeEntries' },
  { test: /^\/attendance\/approvals\/leave/, i18nKey: 'leaveApprovals' },
  { test: /^\/attendance\/approvals\/overtime/, i18nKey: 'overtimeApprovals' },
  { test: /^\/attendance\/approvals\/missed-clock-in/, i18nKey: 'missedClockInApprovals' },
  { test: /^\/attendance\/approvals\/business-trip/, i18nKey: 'businessTripApprovals' },
  { test: /^\/attendance\/approvals\/off-site-work/, i18nKey: 'offSiteWorkApprovals' },
  { test: /^\/attendance\/approvals\/schedule-change/, i18nKey: 'scheduleChangeApprovals' },
  { test: /^\/attendance\/approvals/, i18nKey: 'approvals' },
  { test: /^\/attendance\/calculation/, i18nKey: 'attendanceCalculation' },
  { test: /^\/attendance\/reports/, i18nKey: 'attendanceReports' },
  { test: /^\/attendance/, i18nKey: 'attendance' },

  // Processes
  { test: /^\/processes\/overview/, i18nKey: 'processOverview' },
  { test: /^\/processes\/roles/, i18nKey: 'processRoles' },
  { test: /^\/processes\/form-components/, i18nKey: 'formComponents' },
  { test: /^\/processes\/nodes/, i18nKey: 'processNodes' },
  { test: /^\/processes\/designer/, i18nKey: 'processDesigner' },
  { test: /^\/processes\/instances\/active/, i18nKey: 'activeInstances' },
  { test: /^\/processes\/instances\/completed/, i18nKey: 'completedInstances' },
  { test: /^\/processes\/instances\/failed/, i18nKey: 'failedInstances' },
  { test: /^\/processes\/instances\/monitoring/, i18nKey: 'instanceMonitoring' },
  { test: /^\/processes\/instances/, i18nKey: 'processInstances' },
  { test: /^\/processes\/tasks\/my/, i18nKey: 'myProcessTasks' },
  { test: /^\/processes\/tasks\/urgent/, i18nKey: 'urgentTasks' },
  { test: /^\/processes\/tasks\/expiring/, i18nKey: 'expiringTasks' },
  { test: /^\/processes\/tasks\/completed/, i18nKey: 'completedTasks' },
  { test: /^\/processes\/tasks/, i18nKey: 'processTasks' },
  { test: /^\/processes\/requests\/my/, i18nKey: 'myRequests' },
  { test: /^\/processes\/requests\/pending/, i18nKey: 'pendingApprovals' },
  { test: /^\/processes\/requests\/rejected/, i18nKey: 'rejectedRequests' },
  { test: /^\/processes\/requests\/delegated/, i18nKey: 'delegatedRequests' },
  { test: /^\/processes\/requests\/history/, i18nKey: 'requestHistory' },
  { test: /^\/processes\/requests/, i18nKey: 'processRequests' },
  { test: /^\/processes\/analytics/, i18nKey: 'processAnalytics' },
  { test: /^\/processes/, i18nKey: 'processes' },

  // Integrations
  { test: /^\/integrations\/overview/, i18nKey: 'integrationsOverview' },
  { test: /^\/integrations\/connectors/, i18nKey: 'connectors' },
  { test: /^\/integrations\/api/, i18nKey: 'apiKeys' },
  { test: /^\/integrations\/webhooks/, i18nKey: 'webhooks' },
  { test: /^\/integrations/, i18nKey: 'integrations' },

  // System
  { test: /^\/system\/overview/, i18nKey: 'systemOverview' },
  { test: /^\/system\/access-control\/roles/, i18nKey: 'rolesPermissions' },
  { test: /^\/system\/access-control/, i18nKey: 'accessControl' },
  { test: /^\/system\/audit-logs\/access/, i18nKey: 'accessLogs' },
  { test: /^\/system\/audit-logs\/activity/, i18nKey: 'activityLogs' },
  { test: /^\/system\/audit-logs/, i18nKey: 'auditLogs' },
  { test: /^\/system\/settings\/security/, i18nKey: 'securitySettings' },
  { test: /^\/system\/settings\/workers/, i18nKey: 'workerDefaults' },
  { test: /^\/system\/settings\/notifications/, i18nKey: 'notifications' },
  { test: /^\/system\/settings\/data-export/, i18nKey: 'dataExportSettings' },
  { test: /^\/system\/settings/, i18nKey: 'systemSettings' },
  { test: /^\/system\/billing/, i18nKey: 'billing' },
  { test: /^\/system\/support/, i18nKey: 'support' },
  { test: /^\/system/, i18nKey: 'system' },
];

/**
 * The i18n key naming this path, or null when no rule matches.
 *
 * NULL, not a humanized string. This used to fall back to a title-cased last path
 * segment ("Security" for /account/security), which could not work for two
 * reasons: the consumer treats this value as an i18n KEY and looks it up, so a
 * display string never resolves and always rendered the "Page" fallback; and the
 * string was English, so even if it had rendered it would have been an English leak
 * in eight locales. One field carrying two conventions, with the consumer handling
 * only one (svdy-rules §10.5b).
 *
 * Returning null makes the gap honest: the widget shows its generic label, and
 * `src/i18n/recentlyVisitedTitles.test.ts` fails on any MainLayout route that
 * reaches this branch — so the gap is caught at build time rather than read off a
 * dashboard.
 */
export function titleFor(path: string): string | null {
  return TITLE_MAP.find((m) => m.test.test(path))?.i18nKey ?? null;
}
